import React, { useMemo, useCallback } from "react";
import { makeStyles } from "@material-ui/styles";
import { Formik, FieldArray } from "formik";
import {
  createLogEntryPayload,
  withTrackProps,
} from "@dpdgroupuk/react-event-tracker";

import RightDrawerModal from "../../RightDrawerModal";
import SliderCard from "../../SliderCard";
import Button from "../../Button";
import palette from "../../styles/palette";
import CriteriaItem from "./CriteriaItem";
import {
  isButtonsEnabled,
  getDefaultCriteriaItem,
  validateAllCriteria,
  getCriteriaDiff,
} from "./model";
import {
  SELECT_BY_CRITERIA_TITLE,
  CLEAR_ALL_CRITERIA,
  APPLY_CRITERIA,
  ADD_NEW_CRITERIA,
} from "../../../constants/strings";
import { SELECT_STOP_BY_CRITERIA } from "../../../constants/analytics";

const useStyles = makeStyles(({ typography }) => ({
  applyButton: {
    marginLeft: typography.pxToRem(20),
  },
}));

const SelectByCriteriaModal = ({
  open,
  onClose,
  route,
  onSave,
  criteria,
  handleClearClickIds,
  handleAddCriteriaIds,
  handleRemoveCriteriaIds,
  handleSelectCriteriaIds,
}) => {
  const styles = useStyles();

  const initialValues = useMemo(
    () => ({
      criteria: criteria?.length ? criteria : [getDefaultCriteriaItem()],
    }),
    [criteria]
  );

  const isFormDirty = useCallback(
    (data) => getCriteriaDiff(data, initialValues.criteria).length,
    [initialValues.criteria]
  );

  const handleClearClick = useCallback(
    (props) => () => {
      handleClearClickIds();
      props.setValues({ criteria: [getDefaultCriteriaItem()] });
    },
    [handleClearClickIds]
  );

  const handleAddNewCriteria = useCallback(
    (array) => () => {
      handleAddCriteriaIds();
      array.push(getDefaultCriteriaItem());
    },
    [handleAddCriteriaIds]
  );

  const handleRemoveCriteria = useCallback(
    (array, index) => () => {
      handleRemoveCriteriaIds(index);
      array.remove(index);
    },
    [handleRemoveCriteriaIds]
  );

  const handleChangeCriteriaValue = useCallback(
    ({ setFieldValue }) => (name, value) => {
      handleSelectCriteriaIds(value);
      setFieldValue(name, value);
    },
    [handleSelectCriteriaIds]
  );

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={onSave}
      validate={validateAllCriteria}
    >
      {(props) => (
        <RightDrawerModal
          open={open}
          onClose={onClose}
          title={SELECT_BY_CRITERIA_TITLE}
          loadId={SELECT_STOP_BY_CRITERIA.LOAD}
          actions={
            <>
              <Button
                variant="contained"
                bgColor={palette.yellow}
                color={palette.typography.black.high}
                colorDisabled={palette.typography.black.disabled}
                border={`1px solid ${palette.map.key3}`}
                onClick={handleClearClick(props)}
                bgColorDisabled="rgba(255,213,0, .38)"
                disabled={!isButtonsEnabled(props.values.criteria)}
              >
                {CLEAR_ALL_CRITERIA}
              </Button>
              <Button
                variant="contained"
                bgColor={palette.map.key2}
                border={"1px solid rgba(0, 96, 162, .38)"}
                className={styles.applyButton}
                onClick={props.handleSubmit}
                disabled={!isFormDirty(props.values.criteria) || !props.isValid}
                bgColorDisabled="rgba(68,167,223, .38)"
              >
                {APPLY_CRITERIA}
              </Button>
            </>
          }
        >
          <SliderCard title={ADD_NEW_CRITERIA}>
            <FieldArray
              name="criteria"
              render={(arrayHelper) =>
                props.values?.criteria?.map(
                  (criteriaItem, index, allCriteria) => (
                    <CriteriaItem
                      key={criteriaItem.id}
                      onAddClick={handleAddNewCriteria(arrayHelper)}
                      onRemoveClick={handleRemoveCriteria(arrayHelper, index)}
                      prefixName={`criteria[${index}]`}
                      route={route}
                      value={criteriaItem}
                      onValueChange={handleChangeCriteriaValue(props)}
                      last={index === allCriteria.length - 1}
                      first={index === 0}
                    />
                  )
                )
              }
            />
          </SliderCard>
        </RightDrawerModal>
      )}
    </Formik>
  );
};

export default withTrackProps({
  handleClearClickIds: SELECT_STOP_BY_CRITERIA.SELECT_CLEAR_ALL_CRITERIA,
  handleAddCriteriaIds: SELECT_STOP_BY_CRITERIA.SELECT_ADD,
  handleRemoveCriteriaIds: SELECT_STOP_BY_CRITERIA.SELECT_REMOVE,
  onSave: SELECT_STOP_BY_CRITERIA.SELECT_APPLY_CRITERIA,
  handleSelectCriteriaIds: (value) =>
    createLogEntryPayload(SELECT_STOP_BY_CRITERIA.SELECT_CRITERIA, {
      value,
    }),
})(React.memo(SelectByCriteriaModal));
